import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Link, Unlink, Bot as BotIcon, FileSpreadsheet, CheckCircle, AlertCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { realTimeTradingPredictionService, type Bot } from '@/services/RealTimeTradingPredictionService';
import { PatternUpdateService } from '@/services/pattern-update-service';
import { TradingPatternAnalysisService, type TradingFile } from '@/services/TradingPatternAnalysisService';
import { ReAnalysisStatusBadge } from './ReAnalysisStatusBadge';
import { ReAnalysisService } from '@/services/ReAnalysisService';

interface RealTimeBotLinkingCardProps {
  onLinkingChange?: () => void;
  onBotSelect?: (botId: string) => void;
  refreshTrigger?: number;
}

export const RealTimeBotLinkingCard: React.FC<RealTimeBotLinkingCardProps> = ({
  onLinkingChange,
  onBotSelect,
  refreshTrigger
}) => {
  const [bots, setBots] = useState<Bot[]>([]);
  const [tradingFiles, setTradingFiles] = useState<TradingFile[]>([]);
  const [selectedBotId, setSelectedBotId] = useState<string>('');
  const [selectedFileId, setSelectedFileId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [botStatuses, setBotStatuses] = useState<Record<string, boolean>>({});
  const [isDeletingTrades, setIsDeletingTrades] = useState<Record<string, boolean>>({});
  const [isDeletingPatterns, setIsDeletingPatterns] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadData();
  }, [refreshTrigger]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [botsData, filesData] = await Promise.all([
        realTimeTradingPredictionService.getAllBots(),
        TradingPatternAnalysisService.getTradingFiles()
      ]);

      setBots(botsData);
      if (filesData.success && filesData.data) {
        setTradingFiles(filesData.data);
      }

      // Load re-analysis status for all linked bots
      const linkedBotsData = botsData.filter(bot => bot.linked_trading_file_id);
      const statusPromises = linkedBotsData.map(async (bot) => {
        try {
          const statusResult = await ReAnalysisService.checkReAnalysisStatus(bot.id);
          return { botId: bot.id, completed: statusResult.completed };
        } catch (error) {
          console.error(`Error checking status for bot ${bot.id}:`, error);
          return { botId: bot.id, completed: false };
        }
      });

      const statuses = await Promise.all(statusPromises);
      const statusMap = statuses.reduce((acc, { botId, completed }) => {
        acc[botId] = completed;
        return acc;
      }, {} as Record<string, boolean>);

      setBotStatuses(statusMap);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLinkBot = async () => {
    if (!selectedBotId || !selectedFileId) {
      toast.error('Please select both a bot and a trading file');
      return;
    }

    setIsLinking(true);
    try {
      console.log(`🔗 Attempting to link bot ${selectedBotId} to file ${selectedFileId}`);
      await realTimeTradingPredictionService.linkBotToTradingFile(selectedBotId, selectedFileId);
      
      toast.success('Bot linked to trading file successfully');
      setSelectedBotId('');
      setSelectedFileId('');
      
      // Reload data to refresh the linked bots list
      await loadData();
      onLinkingChange?.();
      
      console.log('✅ Bot linking completed and data refreshed');
    } catch (error) {
      console.error('❌ Error linking bot:', error);
      
      let errorMessage = 'Failed to link bot';
      if (error instanceof Error) {
        if (error.message.includes('Permission denied')) {
          errorMessage = 'Permission denied: Unable to link bot. Please check your permissions.';
        } else if (error.message.includes('RLS')) {
          errorMessage = 'Database permission error: Please contact an administrator.';
        } else if (error.message.includes('no rows affected')) {
          errorMessage = 'Unable to update bot: This may be a permission issue.';
        } else {
          errorMessage = `Failed to link bot: ${error.message}`;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLinking(false);
    }
  };

  const handleUnlinkBot = async (botId: string) => {
    // Show confirmation dialog before unlinking (since it deletes all prediction data)
    const confirmed = window.confirm(
      "⚠️ Unlink Bot Warning\n\n" +
      "This will permanently delete ALL real-time prediction data for this bot:\n" +
      "• Current predictions\n" +
      "• Trading history\n" +
      "• Pattern refresh logs\n" +
      "• Pattern update history\n\n" +
      "The bot record will be kept safe. Continue?"
    );
    
    if (!confirmed) return;

    try {
      await realTimeTradingPredictionService.unlinkBotFromTradingFile(botId);
      toast.success('Bot unlinked and prediction data cleaned up successfully');
      await loadData();
      onLinkingChange?.();
    } catch (error) {
      console.error('Error unlinking bot:', error);
      toast.error('Failed to unlink bot');
    }
  };

  const handleDeleteRealTrades = async (botId: string) => {
    // Show confirmation dialog before deleting trades
    const confirmed = window.confirm(
      "⚠️ Delete Real Trades Warning\n\n" +
      "This will permanently delete ALL real-time trading data for this bot:\n" +
      "• Trading history\n" +
      "• Signal records\n\n" +
      "The bot will remain linked to the CSV file. Continue?"
    );
    
    if (!confirmed) return;

    setIsDeletingTrades(prev => ({ ...prev, [botId]: true }));
    try {
      await realTimeTradingPredictionService.clearBotRealTimeTrades(botId);
      toast.success('Real-time trades deleted successfully');
      onLinkingChange?.();
    } catch (error) {
      console.error('Error deleting real trades:', error);
      toast.error('Failed to delete real trades');
    } finally {
      setIsDeletingTrades(prev => ({ ...prev, [botId]: false }));
    }
  };

  const handleClearPatternHistory = async (botId: string) => {
    // Show confirmation dialog before deleting pattern history
    const confirmed = window.confirm(
      "⚠️ Clear Pattern History Warning\n\n" +
      "This will permanently delete ALL pattern update history for this bot:\n" +
      "• Pattern frequency changes\n" +
      "• Success rate updates\n" +
      "• Trading results tracking\n\n" +
      "The bot will remain linked to the CSV file. Continue?"
    );
    
    if (!confirmed) return;

    setIsDeletingPatterns(prev => ({ ...prev, [botId]: true }));
    try {
      await PatternUpdateService.deletePatternUpdatesForBot(botId);
      toast.success('Pattern history cleared successfully');
      onLinkingChange?.();
    } catch (error) {
      console.error('Error clearing pattern history:', error);
      toast.error('Failed to clear pattern history');
    } finally {
      setIsDeletingPatterns(prev => ({ ...prev, [botId]: false }));
    }
  };

  const linkedBots = bots.filter(bot => bot.linked_trading_file_id);
  const unlinkedBots = bots.filter(bot => !bot.linked_trading_file_id);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Link className="h-5 w-5" />
          Bot-CSV File Linking
        </CardTitle>
        <CardDescription>
          Link trading bots to CSV files for real-time pattern analysis and predictions
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Linking Interface */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Select Bot</label>
            <Select value={selectedBotId} onValueChange={setSelectedBotId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a bot..." />
              </SelectTrigger>
              <SelectContent>
                {unlinkedBots.map((bot) => (
                  <SelectItem key={bot.id} value={bot.id}>
                    <div className="flex items-center gap-2">
                      <BotIcon className="h-4 w-4" />
                      {bot.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Select Trading File</label>
            <Select value={selectedFileId} onValueChange={setSelectedFileId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a file..." />
              </SelectTrigger>
              <SelectContent>
                {tradingFiles.map((file) => (
                  <SelectItem key={file.id} value={file.id}>
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet className="h-4 w-4" />
                      {file.filename} ({file.total_trades} trades)
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-end">
            <Button 
              onClick={handleLinkBot}
              disabled={!selectedBotId || !selectedFileId || isLinking}
              className="w-full"
            >
              {isLinking ? 'Linking...' : 'Link Bot'}
            </Button>
          </div>
        </div>

        {/* Linked Bots Table */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Linked Bots</h3>
            <Badge variant="outline">
              {linkedBots.length} bot{linkedBots.length !== 1 ? 's' : ''} linked
            </Badge>
          </div>

          {linkedBots.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-muted-foreground/25 rounded-lg">
              <Link className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-lg font-medium mb-2">No bots linked yet</p>
              <p className="text-sm text-muted-foreground">
                Link your first bot to a trading file to enable real-time predictions
              </p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <ScrollArea orientation="horizontal" className="w-full">
                <div className="min-w-[1100px] p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[140px]">Bot Name</TableHead>
                        <TableHead className="hidden sm:table-cell min-w-[180px]">Signal Token</TableHead>
                        <TableHead className="min-w-[160px]">Linked File</TableHead>
                        <TableHead className="hidden md:table-cell min-w-[100px]">Status</TableHead>
                        <TableHead className="hidden lg:table-cell min-w-[120px]">Re-Analysis</TableHead>
                        <TableHead className="min-w-[320px] sticky right-0 bg-background border-l">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {linkedBots.map((bot) => {
                        const linkedFile = tradingFiles.find(f => f.id === bot.linked_trading_file_id);
                        return (
                          <TableRow key={bot.id}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <BotIcon className="h-4 w-4 flex-shrink-0" />
                                <div className="min-w-0">
                                  <div className="truncate font-medium">{bot.name}</div>
                                  <div className="sm:hidden text-xs text-muted-foreground mt-1">
                                    <Badge variant={bot.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                                      {bot.status}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <code className="bg-muted px-2 py-1 rounded text-xs max-w-[140px] block truncate cursor-help">
                                      {bot.signal_token}
                                    </code>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="font-mono text-xs">{bot.signal_token}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                            <TableCell>
                              {linkedFile ? (
                                <div className="flex items-center gap-2 min-w-0">
                                  <FileSpreadsheet className="h-4 w-4 flex-shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <div className="truncate text-sm font-medium">{linkedFile.filename}</div>
                                    <Badge variant="outline" className="text-xs mt-1">
                                      {linkedFile.total_trades} trades
                                    </Badge>
                                  </div>
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-sm">File not found</span>
                              )}
                            </TableCell>
                            <TableCell className="hidden md:table-cell">
                              <Badge variant={bot.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                {bot.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              <ReAnalysisStatusBadge botId={bot.id} />
                            </TableCell>
                             <TableCell className="sticky right-0 bg-background border-l">
                               <div className="flex flex-wrap gap-1.5 p-1 min-w-[320px]">
                                 <Button
                                   variant={botStatuses[bot.id] ? "secondary" : "default"}
                                   size="sm"
                                   disabled={botStatuses[bot.id]}
                                   onClick={() => onBotSelect?.(bot.id)}
                                   className="text-xs h-7 px-3 flex-shrink-0"
                                 >
                                   {botStatuses[bot.id] ? 'Done' : 'Select'}
                                 </Button>
                                 <Button
                                   variant="destructive"
                                   size="sm"
                                   disabled={isDeletingTrades[bot.id]}
                                   onClick={() => handleDeleteRealTrades(bot.id)}
                                   className="text-xs h-7 px-3 flex-shrink-0"
                                 >
                                   <Trash2 className="h-3 w-3 mr-1" />
                                   <span className="hidden sm:inline">
                                     {isDeletingTrades[bot.id] ? 'Deleting...' : 'Clear Trades'}
                                   </span>
                                   <span className="sm:hidden">
                                     {isDeletingTrades[bot.id] ? 'Del...' : 'Clear'}
                                   </span>
                                 </Button>
                                 <Button
                                   variant="secondary"
                                   size="sm"
                                   disabled={isDeletingPatterns[bot.id]}
                                   onClick={() => handleClearPatternHistory(bot.id)}
                                   className="text-xs h-7 px-3 flex-shrink-0"
                                 >
                                   <Trash2 className="h-3 w-3 mr-1" />
                                   <span className="hidden lg:inline">
                                     {isDeletingPatterns[bot.id] ? 'Clearing...' : 'Clear Patterns'}
                                   </span>
                                   <span className="lg:hidden">
                                     {isDeletingPatterns[bot.id] ? 'Clearing...' : 'Patterns'}
                                   </span>
                                 </Button>
                                 <Button
                                   variant="outline"
                                   size="sm"
                                   onClick={() => handleUnlinkBot(bot.id)}
                                   className="text-xs h-7 px-3 flex-shrink-0"
                                 >
                                   <Unlink className="h-3 w-3 mr-1" />
                                   <span className="hidden sm:inline">Unlink</span>
                                   <span className="sm:hidden">Unlink</span>
                                 </Button>
                               </div>
                             </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </ScrollArea>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};